import {
  isAccessDeniedError,
  protectedDocumentMessage,
  readProtectionType,
} from './protection';

/** Word refused to modify the document (protected, read-only, Protected View).
 *  A distinct type so callers can tell it apart from a model or network error —
 *  nothing about retrying, re-authenticating or changing model helps here. */
export class DocumentProtectedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DocumentProtectedError';
  }
}

/**
 * Every Word call goes through here, which is why the protected-document
 * translation lives here too: Optimize, Review's quick-fix, Draft's apply, the
 * checks and the text-editing actions all plant anchors or write ranges, and all
 * of them failed with the same unexplained permission error on a protected
 * document.
 */
export async function runBatch(
  callback: (context: Word.RequestContext) => Promise<void>,
): Promise<void> {
  try {
    await Word.run(callback);
  } catch (error) {
    console.error('Word.run error:', error);
    if (isAccessDeniedError(error)) {
      // Word refused, not the AI service. Say so — the raw RichApi wording
      // ("You don't have sufficient permissions for this action") is
      // indistinguishable from an access problem with the gateway, and reading
      // it that way cost a day of chasing keys and model permissions.
      const protectionType = await readProtectionType();
      throw new DocumentProtectedError(protectedDocumentMessage(protectionType));
    }
    throw error;
  }
}

export async function getDocumentBody(): Promise<string> {
  let text = '';
  await runBatch(async (context) => {
    const body = context.document.body;
    body.load('text');
    await context.sync();
    text = body.text;
  });
  return text;
}

/** A paragraph with its computed list numbering. Section numbers in contracts
 *  are often auto-numbered (Word list numbering) and do NOT appear in
 *  `paragraph.text`; the number is only in `ListItem.listString` (e.g. "7.4").
 *  Reading both lets the cross-reference checker know which sections exist. */
export interface NumberedParagraph {
  text: string;
  /** Computed list number for an auto-numbered paragraph, or null. */
  listString: string | null;
}

/** Read every body paragraph with its list number (WordApi 1.3, GA). */
export async function readNumberedParagraphs(): Promise<NumberedParagraph[]> {
  const out: NumberedParagraph[] = [];
  await runBatch(async (context) => {
    const paras = context.document.body.paragraphs;
    paras.load('text');
    await context.sync();

    const listItems = paras.items.map((p) => p.listItemOrNullObject);
    for (const li of listItems) li.load('isNullObject,listString');
    await context.sync();

    for (let i = 0; i < paras.items.length; i++) {
      const li = listItems[i];
      out.push({
        text: (paras.items[i].text ?? '').trim(),
        listString: li.isNullObject ? null : (li.listString ?? null),
      });
    }
  });
  return out;
}

export async function getDocumentProperties(): Promise<{
  title: string;
  author: string;
}> {
  let title = '';
  let author = '';
  await runBatch(async (context) => {
    const props = context.document.properties;
    props.load('title,author');
    await context.sync();
    title = props.title;
    author = props.author;
  });
  return { title, author };
}

/**
 * Count the tracked changes (revisions) in the document or current selection,
 * using Word's authoritative getTrackedChanges() API (WordApi 1.6) — this
 * matches exactly what the Reviewing Pane / Accept-All shows, so it never
 * false-positives on clean-but-field-heavy documents. Returns 0 on hosts
 * without the API (best-effort). Read-only.
 */
export async function countTrackedChanges(scope: 'document' | 'selection'): Promise<number> {
  let count = 0;
  try {
    await runBatch(async (context) => {
      const target =
        scope === 'selection' ? context.document.getSelection() : context.document.body;
      if (typeof (target as { getTrackedChanges?: unknown }).getTrackedChanges !== 'function') return;
      const changes = (target as unknown as { getTrackedChanges(): Word.TrackedChangeCollection }).getTrackedChanges();
      changes.load('items');
      await context.sync();
      count = changes.items.length;
    });
  } catch {
    /* getTrackedChanges unavailable / failed → treat as unknown (0) */
  }
  return count;
}
